/**
 * PrivacyPolicy — V2.M.1
 *
 * Minimal, honest privacy notice describing exactly what the
 * analytics tracker captures. Linked from the footer; no banner,
 * no pop-up — but available for anyone who looks.
 *
 * Style aligné V2.L.1 (Stripe-inspired clean light) : fond clair,
 * texte sobre, sections délimitées par des bordures fines.
 */

import { useTranslation } from 'react-i18next';

export function PrivacyPolicy() {
  const { t } = useTranslation();

  return (
    <main
      style={{
        maxWidth: '780px',
        margin: '0 auto',
        padding: '40px 24px 80px',
        color: 'var(--text)',
        lineHeight: 1.6,
      }}
    >
      <h1 style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px' }}>
        {t('privacy.title', 'Confidentialité & données collectées')}
      </h1>
      <p style={{ color: 'var(--muted)', fontSize: '14px', marginBottom: '32px' }}>
        {t(
          'privacy.lastUpdated',
          'Dernière mise à jour : juin 2026',
        )}
      </p>

      <Section title={t('privacy.summary.title', 'Résumé')}>
        <p>
          {t(
            'privacy.summary.body',
            'app.edouard.ai collecte des données techniques d\'utilisation pour comprendre comment l\'application est utilisée et l\'améliorer. Aucune donnée n\'est revendue. Les données ne sont accessibles qu\'à l\'opérateur du service via une interface d\'administration protégée.',
          )}
        </p>
      </Section>

      <Section title={t('privacy.what.title', 'Ce que nous collectons')}>
        <ul style={{ paddingLeft: '20px' }}>
          <li>{t('privacy.what.ip', 'Adresse IP (utilisée pour compter les visiteurs uniques et localisation géographique au niveau pays/région)')}</li>
          <li>{t('privacy.what.ua', 'Type de navigateur et système d\'exploitation (User-Agent)')}</li>
          <li>{t('privacy.what.pages', 'Pages visitées et navigation au sein de l\'application')}</li>
          <li>{t('privacy.what.tokens', 'Tokens crypto ajoutés ou suivis dans le portfolio')}</li>
          <li>{t('privacy.what.wallets', 'Adresses de wallets liées via les pages Monitor (Hedera, Cronos, etc.)')}</li>
          <li>{t('privacy.what.session', 'Identifiant de session anonyme (UUID stocké dans votre navigateur)')}</li>
          <li>{t('privacy.what.account', 'Si vous avez créé un compte : votre identifiant utilisateur Supabase associé à la session')}</li>
        </ul>
      </Section>

      <Section title={t('privacy.why.title', 'Pourquoi nous collectons ces données')}>
        <ul style={{ paddingLeft: '20px' }}>
          <li>{t('privacy.why.usage', 'Comprendre quelles fonctionnalités sont utilisées et lesquelles le sont peu')}</li>
          <li>{t('privacy.why.adoption', 'Mesurer l\'adoption des nouvelles features (monitors, supply breakdowns, etc.)')}</li>
          <li>{t('privacy.why.tokens', 'Identifier les tokens et chaînes les plus suivis pour prioriser le roadmap')}</li>
          <li>{t('privacy.why.bugs', 'Détecter des patterns d\'erreur ou d\'abandon')}</li>
        </ul>
      </Section>

      <Section title={t('privacy.storage.title', 'Où et comment les données sont stockées')}>
        <p>
          {t(
            'privacy.storage.body',
            'Les données sont stockées dans une base PostgreSQL hébergée par Supabase. Les politiques d\'accès (RLS) empêchent toute lecture depuis votre navigateur — seul un endpoint serveur protégé par clé d\'administration peut interroger la base.',
          )}
        </p>
      </Section>

      <Section title={t('privacy.rights.title', 'Vos droits')}>
        <p>
          {t(
            'privacy.rights.body',
            'Conformément au RGPD, vous pouvez à tout moment demander l\'accès, la rectification ou la suppression de vos données personnelles. Pour exercer ces droits, contactez l\'opérateur du service.',
          )}
        </p>
        <p style={{ marginTop: '12px' }}>
          {t('privacy.rights.contact', 'Contact : ')}
          <a
            href="mailto:contact@edouard.ai"
            style={{ color: 'var(--accent)', textDecoration: 'underline' }}
          >
            contact@edouard.ai
          </a>
        </p>
      </Section>

      <Section title={t('privacy.thirdParty.title', 'Services tiers')}>
        <p>
          {t(
            'privacy.thirdParty.body',
            'En complément de notre tracking interne, le site utilise Vercel Analytics et Vercel Speed Insights (métriques agrégées, sans cookies). Aucun autre service de tracking publicitaire n\'est utilisé.',
          )}
        </p>
      </Section>

      <Section title={t('privacy.changes.title', 'Modifications de cette politique')}>
        <p>
          {t(
            'privacy.changes.body',
            'Cette politique peut être mise à jour au fil de l\'évolution du service. La date de dernière mise à jour est indiquée en haut de cette page.',
          )}
        </p>
      </Section>
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        borderTop: '1px solid var(--border)',
        paddingTop: '20px',
        paddingBottom: '20px',
      }}
    >
      <h2
        style={{
          fontSize: '17px',
          fontWeight: 600,
          marginBottom: '12px',
          color: 'var(--text)',
        }}
      >
        {title}
      </h2>
      <div style={{ fontSize: '14px', color: 'var(--text-secondary, var(--text))' }}>
        {children}
      </div>
    </section>
  );
}
